import React, { useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";

import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import FormBox from "../../component/FormBox/FormBox";
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    InputLabel,
    makeStyles,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import errorWarning from "../../images/icon_warning.gif";
import { isEmpty, isNotEmpty } from "../../Utils";
import { useDispatch, useSelector } from "react-redux";
import { setCurrentStep, setFieldValues } from "../../redux/action";
import { getFieldValuesByStep, getFormId } from "../../redux/selector";
import CustomDatePicker from "../../component/CustomDatePicker/CustomDatePicker";
import moment from "moment";
import CustomTextField from "../../inputComponent/CustomTextField/CustomTextField";

const useStyles = makeStyles({
    table: {
        minWidth: 650,
    },
});

export default function TableInputSample() {
    const formId = useSelector(getFormId());
    const stepName = "TableInput";

    const { t, i18n } = useTranslation();

    const classes = useStyles();

    const createFoodDefaultValues = () => ({
        calories: null,
        fat: null,
        carbs: null,
        protein: null,
        price: null,
    });

    const dispatch = useDispatch();
    let defaultValues = useSelector(getFieldValuesByStep(stepName));
    const initialDefaultValues = {
        frozenYoghurt: createFoodDefaultValues(),
        iceCreamSandwich: createFoodDefaultValues(),
        eclair: createFoodDefaultValues(),
        cupcake: createFoodDefaultValues(),
        gingerbread: createFoodDefaultValues(),
    };
    // frozenYoghurt:{
    //     calories: null,
    //     fat: null,
    //     carbs: null,
    //     protein: null,
    //     price: null,
    // }
    if (isEmpty(defaultValues)) {
        defaultValues = initialDefaultValues;
    }

    const createFoodYupValidation = () =>
        yup.object().shape({
            calories: yup.number(),
            fat: yup.number(),
            carbs: yup.number(),
            protein: yup.number(),
            price: yup.number(),
        });

    const yupSchema = yup.object().shape({
        frozenYoghurt: createFoodYupValidation(),
        iceCreamSandwich: createFoodYupValidation(),
        eclair: createFoodYupValidation(),
        cupcake: createFoodYupValidation(),
        gingerbread: createFoodYupValidation(),
    });

    const {
        register,
        handleSubmit,
        control,
        formState,
        clearErrors,
        reset,
        watch,
        getValues,
        setValue,
    } = useForm({
        defaultValues,
        mode: "onBlur",
        resolver: yupResolver(yupSchema),
    });

    const onStepSubmit = (formData) => {
        //onStepSubmit
        console.log("onStepSubmit, formData:", formData);
        dispatch(setFieldValues(stepName, formData));
        dispatch(setCurrentStep("Confirmation"));
    };

    const onStepError = (errors) => {
        //onStepError
        console.log("onStepError, errors: ", errors);
    };

    const onPrev = () => {};

    const onReset = () => {
        reset(initialDefaultValues);
    };

    const errors = formState.errors;

    return (
        <form noValidate>
            <FormBox header="Table Input">
                <TableContainer>
                    <Table className={classes.table} aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                <TableCell>Dessert (100g serving)</TableCell>
                                <TableCell align="right">Calories</TableCell>
                                <TableCell align="right">
                                    Fat&nbsp;(g)
                                </TableCell>
                                <TableCell align="right">
                                    Carbs&nbsp;(g)
                                </TableCell>
                                <TableCell align="right">
                                    Protein&nbsp;(g)
                                </TableCell>
                                <TableCell align="right">Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[
                                "frozenYoghurt",
                                "iceCreamSandwich",
                                "eclair",
                                "cupcake",
                                "gingerbread",
                            ].map((key) => (
                                <TableRow key={key}>
                                    <TableCell component="th" scope="row">
                                        {t(`sample.table.${key}`)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <CustomTextField
                                            inputProps={{
                                                ...register(`${key}.calories`),
                                                style: {
                                                    textAlign: "right",
                                                },
                                            }}
                                            error={errors?.[`${key}.calories`]}
                                            showError={false}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <CustomTextField
                                            inputProps={{
                                                ...register(`${key}.fat`),
                                                style: {
                                                    textAlign: "right",
                                                },
                                            }}
                                            error={errors?.[`${key}.fat`]}
                                            showError={false}
                                            unit="g"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <CustomTextField
                                            inputProps={{
                                                ...register(`${key}.carbs`),
                                                style: {
                                                    textAlign: "right",
                                                },
                                            }}
                                            error={errors?.[`${key}.carbs`]}
                                            showError={false}
                                            unit="g"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <CustomTextField
                                            inputProps={{
                                                ...register(`${key}.protein`),
                                                style: {
                                                    textAlign: "right",
                                                },
                                            }}
                                            error={errors?.[`${key}.protein`]}
                                            showError={false}
                                            unit="g"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <CustomTextField
                                            inputProps={{
                                                ...register(`${key}.price`),
                                                style: {
                                                    textAlign: "right",
                                                },
                                            }}
                                            error={errors?.[`${key}.price`]}
                                            showError={false}
                                            prefixUnit="$"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </FormBox>
            <FormBox>
                <Box display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        color="secondary"
                        className="secBtn"
                        onClick={() => onPrev()}
                    >
                        {t("common.button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        color="secondary"
                        className="secBtn"
                        onClick={() => onReset()}
                    >
                        {t("common.button.clear")}
                    </Button>
                    <Button variant="contained" color="primary" type="submit">
                        {t("common.button.next")}
                    </Button>
                </Box>
            </FormBox>
        </form>
    );
}
